/**
 * グラフ描画共通処理
 */
import color from "../../scss/color.scss";
import { setInterval, clearInterval } from "timers";

export default {
  name: "lib-common-graph",
  computeViewGraphWidth,
  computeViewLabelWidth,
  computeImageGraphWidth,
  computeImageLegendWidth,
  computeGraphWidth,
  computeGraphUnevenWidth,
  computeLabelWidth,
  computeHeight,
  computeTimeLineBeforeOffset,
  computeTimeLineAfterOffset,
  computeTitleSize,
  makeGraphSvgTag,
  makeGraphUnevenSvgTag,
  addGraphGroupTag,
  addGraphFrameTag,
  addGraphFrameUnevenTag,
  addGraphXAxisLabelTag,
  addGraphXAxisLabelUnevenTag,
  makeLabelSvgTag,
  makeToolTipSvgTag,
  makeBarGraphSvgTag,
  makeLineGraphSvgTag,
  makeCircleSvgTag,
  makeBorderGraphSvgTag,
  addLeftLabelGroupTag,
  addLeftLabelYAxisLabelTag,
  addLeftLabelYAxisTitleTag,
  addLeftLabelYAxisMarkerTag,
  addRightLabelGroupTag,
  addRightLabelYAxisLabelTag,
  addRightLabelYAxisTitleTag,
  addRightLabelYAxisTitleTagMulti,
  addRightLabelYAxisMarkerTag,
  makeLegendSvgTag,
  addLegendGroupTag,
  makeLegendFrame,
  makeLegendLine,
  addLegendTitle,
  addLegendGraph,
  addLegendBarGraph,
  addLegendBarGraphUnit,
  addLegendLineGraph,
  addLegendLineGraphUnit,
  addObsInfo,
  addStyleTag,
  makeImageSvgTag,
  startScrollGraph,
  stopScrollGraph,
};

// 文字列リソース
export let str = {
  CRLF: "\r\n",
  Sept: ":",
  Div: "・",
  Warning: "警戒値",
  Caution: "注意値",
  Reference: "基準値",
  Over: "以上",
  Interval: "～",
  Under: "未満",
  Loss: "欠測",
  None: "--",
};

// 描画単位
const viewUnit = "px";
// 全体/拡大描画比率（拡大=全体の5倍）
const viewRatio = 5;
// 描画領域幅
const viewWidth = 1800;
// ラベル描画領域幅
const viewLabelWidth = 300;
// 表示グラフ幅比率(75%)
const viewGraphRatio = 0.75;
// 表示ラベル幅比率(12.5%)
const viewLabelRatio = 0.125;
// 日付表示位置調整値
const dateLabelPosY = 50;
// 時刻表示位置調整値
const timeLabelPosY = 95;
// 棒グラフ観測点サイズ
const circleSize = 9;
// ラベル表示位置調整値
const labelPosX = 40;
// ラベル表示位置調整値
const labelPosY = 15;
// マーカーサイズ横幅
const markerSizeX = 28;
// マーカーサイズ縦幅（中心点から上下に展開）
const markerSizeY = 20;
// 凡例フレーム丸み
const legendFrameRadius = 16;
// 凡例のタイトル基準位置（x座標）
const legendTitlePosX = 25;
// 凡例のグラフ基準位置（x座標）
const legendGraphPosX = 40;
// 凡例の説明基準位置（x座標）
const legendDescPosX = 160;
// 凡例の1行あたりの高さ（y）
const legendTextHeight = 70;
// 観測所情報の基準位置（x座標）
const obsInfoPosX = 40;
// 観測所情報の高さ（y）
const obsInfoHeight = 100;
// グラフの共通スタイル
const styleCommon = `<![CDATA[
.kwg-frame {
  fill: ghostwhite;
  stroke: black;
  stroke-width: 1;
}
.kwg-frame-prediction {
  fill: ${color.stagePrediction};
  stroke: ${color.stagePrediction};
  stroke-width: 1;
}
.kwg-border-x {
  stroke: dimgray;
  stroke-width: 1;
}
.kwg-border-x-none {
  stroke: ghostwhite;
  stroke-width: 1;
}
.kwg-border-y {
  stroke: dimgray;
  stroke-width: 1;
  stroke-dasharray: 5 7;
}
.kwg-tip {
  fill: transparent;
  stroke: transparent;
  stroke-width: 1;
}
.kwg-graph-label {
  fill: black;
  font-weight: 550;
  font-size: 48px;
  font-family: sans-serif;
}
.kwg-obsinfo-label {
  fill: black;
  font-weight: 500;
  font-size: 54px;
}
]]>`;

/**
 * グラフ表示領域の幅算出
 * @param {*} width 親要素の幅
 */
function computeViewGraphWidth(width) {
  // グラフの割合を掛ける
  return width * viewGraphRatio;
}

/**
 * ラベル表示領域の幅算出
 * @param {*} width 親要素の幅
 */
function computeViewLabelWidth(width) {
  // ラベルの割合を掛ける
  return width * viewLabelRatio;
}

/**
 * 画像出力用グラフ表示領域の幅算出
 * @param {*} width 親要素の幅
 * @param {*} size 凡例の幅
 */
function computeImageGraphWidth(width, size) {
  // 凡例の割合を求める
  let w = viewWidth + viewLabelWidth * 2 + size;
  let ratio = (viewWidth + viewLabelWidth * 2) / w;
  return width * ratio;
}

/**
 * 画像出力用凡例表示領域の幅算出
 * @param {*} width 親要素の幅
 * @param {*} size 凡例の幅
 */
function computeImageLegendWidth(width, size) {
  // 凡例の割合を求める
  let ratio = size / (viewWidth + viewLabelWidth * 2 + size);
  return width * ratio;
}

/**
 * グラフ表示領域の幅算出
 * @param {*} width 親要素の幅
 * @param {*} isExp true=拡大, false=全体
 */
function computeSvgGraphWidth(width, isExp) {
  if (isExp) {
    // 拡大表示
    return width * viewGraphRatio * viewRatio;
  }
  // 全体表示
  return width * viewGraphRatio;
}

/**
 * グラフ描画領域の幅算出（時間軸不均等）
 * @param {*} width 親要素の幅
 * @param {*} isExp true=拡大, false=全体
 * @param {*} num データ数
 */
function computeSvgGraphUnevenWidth(width, isExp, num) {
  // 全体表示
  let w = width * viewGraphRatio;
  if (isExp) {
    // 拡大表示
    w = (width * viewGraphRatio * viewRatio * num) / 50;
    if (w < width * viewGraphRatio) {
      w = width * viewGraphRatio;
    }
  }

  return w;
}

/**
 * グラフ描画領域の幅算出
 * @param {*} isExp true=拡大, false=全体
 */
function computeGraphWidth(isExp) {
  if (isExp) {
    // 拡大表示
    return viewWidth * viewRatio;
  }
  // 全体表示
  return viewWidth;
}

/**
 * グラフ描画領域の幅算出（時間軸不均等）
 * @param {*} isExp true=拡大, false=全体
 * @param {*} num データ数
 */
function computeGraphUnevenWidth(isExp, num) {
  // 全体表示
  let w = viewWidth;
  if (isExp) {
    // 拡大表示
    w = (viewWidth * viewRatio * num) / 50;
    if (w < viewWidth) {
      w = viewWidth;
    }
  }

  return w;
}

/**
 * ラベル描画領域の幅算出
 */
function computeLabelWidth() {
  // ラベル描画領域は固定
  return viewLabelWidth;
}

/**
 * グラフ描画領域の高さ算出
 * @param {*} width 親要素の幅
 * @param {*} height 親要素の高さ
 */
function computeHeight(width, height) {
  let w = viewWidth + viewLabelWidth * 2;
  // グラフ描画領域の高さは親要素の高さ比と合わせる
  return height * (w / width);
}

/**
 * 縦書き用に半角数字を全角数字に変換
 * @param {*} str 変換前文字列
 * @return 変換後文字列
 */
function replaceString(str) {
  // 半角数字抽出
  return String(str).replace(/[0-9]/g, function(s) {
    // 全角に変換
    return String.fromCharCode(s.charCodeAt(0) + 0xfee0);
  });
}

/**
 * 時間軸描画余白算出
 * @param {*} isExp 拡大/全体表示（true=拡大, false=全体）
 * @param {*} isHour 時間/10分表示（true=時間, false=10分）
 * @param {*} time 表示時刻（hh:mm）
 * @return 余白時刻数
 */
function computeTimeLineBeforeOffset(isExp, isHour, time) {
  if (!isExp) {
    let hour = Number(String(time).substr(0, 2));
    let min = Number(String(time).substr(3, 2));
    if (isHour) {
      return Number(6 - ((hour % 6) + 1));
    } else {
      return Number(6 - (Number(Number(min / 10).toFixed(0)) + 1));
    }
  }
  return 0;
}

/**
 * 時間軸描画余白算出
 * @param {*} isExp 拡大/全体表示（true=拡大, false=全体）
 * @param {*} isHour 時間/10分表示（true=時間, false=10分）
 * @param {*} time 表示時刻（hh:mm）
 * @return 余白時刻数
 */
function computeTimeLineAfterOffset(isExp, isHour, time) {
  if (!isExp) {
    let hour = Number(String(time).substr(0, 2));
    let min = Number(String(time).substr(3, 2));
    if (isHour) {
      return Number(hour % 6 == 0 ? 6 : Number(hour % 6));
    } else {
      return Number(min == 0 ? 6 : Number(min / 10).toFixed(0));
    }
  }
  return 0;
}

/**
 * 見出し文字サイズ算出
 * @param {*} width 親要素の幅
 * @param {*} height 親要素の高さ
 * @return 補正後の文字サイズ
 */
function computeTitleSize(width, height, size, weight) {
  let ret = {
    size: size,
    weight: weight,
  };

  if (width < height) {
    ret.size = ret.size + 16;
    ret.weight = ret.weight + 100;
  }

  if (width / 2.2 > height) {
    ret.size = ret.size - 16;
    ret.weight = ret.weight - 100;
  }

  if (width / 3 > height) {
    ret.size = ret.size - 8;
    ret.weight = ret.weight - 100;
  }

  return ret;
}

/********** **********/

/**
 * グラフSVGタグ生成
 * @param {*} width 親要素の幅
 * @param {*} height 親要素の高さ
 * @param {*} isExp true=拡大, false=全体
 * @return HTMLElement svgタグ
 */
function makeGraphSvgTag(width, height, isExp) {
  // svgタグ要素生成
  let attrViewBox =
    "0 0 " + computeGraphWidth(isExp) + " " + computeHeight(width, height);
  let attrWidth = computeSvgGraphWidth(width, isExp) + viewUnit;
  let attrHeight = height + viewUnit;
  // svgタグ生成
  let tag = document.createElement("svg");
  tag.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  tag.setAttribute("viewBox", attrViewBox);
  tag.setAttribute("width", attrWidth);
  tag.setAttribute("height", attrHeight);
  // styleタグ追加
  addStyleTag(tag, styleCommon);

  return tag;
}

/**
 * グラフSVGタグ生成（時間軸不均等）
 * @param {*} width 親要素の幅
 * @param {*} height 親要素の高さ
 * @param {*} isExp true=拡大, false=全体
 * @param {*} num データ数
 * @return HTMLElement svgタグ
 */
function makeGraphUnevenSvgTag(width, height, isExp, num) {
  // svgタグ要素生成
  let attrViewBox =
    "0 0 " +
    computeGraphUnevenWidth(isExp, num) +
    " " +
    computeHeight(width, height);
  let attrWidth = computeSvgGraphUnevenWidth(width, isExp, num) + viewUnit;
  let attrHeight = height + viewUnit;
  // svgタグ生成
  let tag = document.createElement("svg");
  tag.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  tag.setAttribute("viewBox", attrViewBox);
  tag.setAttribute("width", attrWidth);
  tag.setAttribute("height", attrHeight);
  // styleタグ追加
  addStyleTag(tag, styleCommon);

  return tag;
}

/**
 * グラフグループ生成
 * @param {*} parent 親要素
 * @return HTMLElement gタグ
 */
function addGraphGroupTag(parent) {
  // gタグ生成
  let tag = document.createElement("g");
  tag.setAttribute("id", "svg-graph");
  tag.setAttribute("class", "svg-graph");
  // 親要素に追加
  parent.appendChild(tag);

  return tag;
}

/**
 * グラフフレーム追加
 * @param {*} parent 親要素
 * @param {*} left グラフ描画領域左端
 * @param {*} top グラフ描画領域上端
 * @param {*} width グラフ領域の幅
 * @param {*} height グラフ領域の高さ
 * @param {*} values 観測値
 * @param {*} isExp 拡大/全体表示（true=拡大, false=全体）
 * @param {*} isHour 時間/10分表示（true=時間, false=10分）
 * @param {*} div 分割数
 * @param {*} prediction 予測件数
 */
function addGraphFrameTag(
  parent,
  left,
  top,
  width,
  height,
  values,
  isExp,
  isHour,
  div,
  prediction
) {
  if (!prediction) prediction = 0;
  let tag;
  // フレーム要素生成
  let right = left + width;
  let bottom = top + height;
  // フレームタグ生成
  tag = document.createElement("rect");
  tag.setAttribute("x", left);
  tag.setAttribute("y", top);
  tag.setAttribute("width", width);
  tag.setAttribute("height", height);
  tag.setAttribute("class", "kwg-frame");
  // 親要素に追加
  parent.appendChild(tag);

  let before = computeTimeLineBeforeOffset(isExp, isHour, values[0].time);
  let after = computeTimeLineAfterOffset(
    isExp,
    isHour,
    values[values.length - 1].time
  );
  let xs = width / (values.length + before + after);
  let x = right - xs * (before + 1);
  let hour, min;

  // 予測件数
  if (prediction > 0) {
    tag = document.createElement("rect");
    tag.setAttribute("x", left + width - xs * (prediction + before));
    tag.setAttribute("y", top + 2);
    tag.setAttribute("width", xs * (prediction + before) - 2);
    tag.setAttribute("height", height - 4);
    tag.setAttribute("class", "kwg-frame-prediction");
    // 親要素に追加
    parent.appendChild(tag);
  }

  // 時間軸
  for (let index = 0; index < values.length; index++, x = x - xs) {
    hour = String(values[index].time).substr(0, 2);
    min = String(values[index].time).substr(3, 2);
    // 拡大表示の場合は全ての時刻に線を描画する
    // 全体表示で10分表示の場合は正時のみ線を描画する
    // 全体表示で時間表示の場合は6:00間隔で線を描画する
    if (
      isExp ||
      (!isExp && !isHour && min == "00") ||
      (!isExp && isHour && hour % 6 == 0 && min == "00")
    ) {
      // 時間軸目盛線生成
      tag = document.createElement("line");
      tag.setAttribute("x1", x);
      tag.setAttribute("x2", x);
      tag.setAttribute("y1", top);
      tag.setAttribute("y2", bottom);
      tag.setAttribute("class", "kwg-border-x");
      // 親要素に追加
      parent.appendChild(tag);
    }
  }

  // 観測値軸
  let ys = height / div;
  let y = top;
  for (let cnt = 1; cnt < div; cnt++) {
    y = y + ys;
    // 観測値軸目盛線生成
    tag = document.createElement("line");
    tag.setAttribute("x1", left);
    tag.setAttribute("x2", right);
    tag.setAttribute("y1", y);
    tag.setAttribute("y2", y);
    tag.setAttribute("class", "kwg-border-y");
    // 親要素に追加
    parent.appendChild(tag);
  }
}

/**
 * グラフフレーム追加（時間軸不均等）
 * @param {*} parent 親要素
 * @param {*} left グラフ描画領域左端
 * @param {*} top グラフ描画領域上端
 * @param {*} width グラフ領域の幅
 * @param {*} height グラフ領域の高さ
 * @param {*} values 観測値
 * @param {*} isExp 拡大/全体表示（true=拡大, false=全体）
 * @param {*} div 分割数
 */
function addGraphFrameUnevenTag(
  parent,
  left,
  top,
  width,
  height,
  values,
  isExp,
  div
) {
  let tag;
  // フレーム要素生成
  let right = left + width;
  let bottom = top + height;
  // フレームタグ生成
  tag = document.createElement("rect");
  tag.setAttribute("x", left);
  tag.setAttribute("y", top);
  tag.setAttribute("width", width);
  tag.setAttribute("height", height);
  tag.setAttribute("class", "kwg-frame");
  // 親要素に追加
  parent.appendChild(tag);

  let xs = width / (values.length + 1);
  let x = right - xs;
  let min;

  // 時間軸
  for (let index = 0; index < values.length; index++, x = x - xs) {
    min = String(values[index].time).substr(3, 2);
    // 拡大表示の場合は全ての時刻に線を描画する
    // 全体表示の場合は正時のみ線を描画する
    if (isExp || min == "00") {
      // 時間軸目盛線生成
      tag = document.createElement("line");
      tag.setAttribute("x1", x);
      tag.setAttribute("x2", x);
      tag.setAttribute("y1", top);
      tag.setAttribute("y2", bottom);
      tag.setAttribute("class", "kwg-border-x");
      // 親要素に追加
      parent.appendChild(tag);
    }
  }

  // 観測値軸
  let ys = height / div;
  let y = top;
  for (let cnt = 1; cnt < div; cnt++) {
    y = y + ys;
    // 観測値軸目盛線生成
    tag = document.createElement("line");
    tag.setAttribute("x1", left);
    tag.setAttribute("x2", right);
    tag.setAttribute("y1", y);
    tag.setAttribute("y2", y);
    tag.setAttribute("class", "kwg-border-y");
    // 親要素に追加
    parent.appendChild(tag);
  }
}

/**
 * グラフ時間軸ラベル追加
 * @param {*} parent 親要素
 * @param {*} left グラフ描画領域左端
 * @param {*} top 時間軸ラベル表示位置上端
 * @param {*} width グラフ領域の幅
 * @param {*} values 観測値
 * @param {*} isExp 拡大/全体表示（true=拡大, false=全体）
 * @param {*} isHour 時間/10分表示（true=時間, false=10分）
 * @param {*} prediction 予測件数
 */
function addGraphXAxisLabelTag(
  parent,
  left,
  top,
  width,
  values,
  isExp,
  isHour,
  prediction
) {
  if (!prediction) prediction = 0;
  let tag, child;
  let before = computeTimeLineBeforeOffset(isExp, isHour, values[0].time);
  let after = computeTimeLineAfterOffset(
    isExp,
    isHour,
    values[values.length - 1].time
  );
  let xs = width / (values.length + before + after);
  let x = width - xs * (before + 1);
  let hour, min;
  let ratio = isExp ? 2 : 12;
  let plot = -1 * ratio;
  for (let index = 0; index < values.length; index++, x = x - xs) {
    hour = String(values[index].time).substr(0, 2);
    min = String(values[index].time).substr(3, 2);
    // 表示間隔以上空いていること
    // 拡大表示の場合は2時刻おきに線を描画する
    // 全体表示で10分表示の場合は2:00間隔で線を描画する
    // 全体表示で時間表示の場合は12:00間隔で線を描画する
    if (
      index >= plot + ratio &&
      ((isExp && index % 2 == 0) ||
        (!isExp && !isHour && hour % 2 == 0 && min == "00") ||
        (!isExp && isHour && hour % 12 == 0 && min == "00"))
    ) {
      // 日付は前表示と異なる場合のみ表示
      if (
        index >= values.length - ratio ||
        values[index + ratio].date != values[index].date
      ) {
        tag = document.createElement("text");
        tag.setAttribute("x", x);
        tag.setAttribute("y", top + dateLabelPosY);
        tag.setAttribute("text-anchor", "middle");
        tag.setAttribute("class", "kwg-graph-label");
        child = document.createTextNode(String(values[index].date).substr(5));
        tag.appendChild(child);
        // 親要素に追加
        parent.appendChild(tag);
      }
      // 時刻表示
      tag = document.createElement("text");
      tag.setAttribute("x", x);
      tag.setAttribute("y", top + timeLabelPosY);
      tag.setAttribute("text-anchor", "middle");
      tag.setAttribute("class", "kwg-graph-label");
      child = document.createTextNode(values[index].time);
      tag.appendChild(child);
      // 親要素に追加
      parent.appendChild(tag);

      plot = index;
    }
  }
}

/**
 * グラフ時間軸ラベル追加（時間軸不均等）
 * @param {*} parent 親要素
 * @param {*} left グラフ描画領域左端
 * @param {*} top 時間軸ラベル表示位置上端
 * @param {*} width グラフ領域の幅
 * @param {*} values 観測値
 * @param {*} isExp 拡大/全体表示（true=拡大, false=全体）
 */
function addGraphXAxisLabelUnevenTag(parent, left, top, width, values, isExp) {
  let tag, child;
  let num = values.length;
  let xs = width / (num + 1);
  let x = width - xs;
  let ratio = 1;
  let plot = 0;
  if (!isExp) {
    if (num > 60) {
      ratio = 12;
    } else if (num > 20) {
      ratio = 6;
    } else {
      ratio = 2;
    }
    plot = ratio / 2;
  }
  for (let index = 0; index < num; index++, x = x - xs) {
    // 表示間隔以上空いていること
    if (index % ratio == plot) {
      // 日付は前表示と異なる場合のみ表示
      if (
        index >= num - ratio ||
        values[index + ratio].date != values[index].date
      ) {
        tag = document.createElement("text");
        tag.setAttribute("x", x);
        tag.setAttribute("y", top + dateLabelPosY);
        tag.setAttribute("text-anchor", "middle");
        tag.setAttribute("class", "kwg-graph-label");
        child = document.createTextNode(String(values[index].date).substr(5));
        tag.appendChild(child);
        // 親要素に追加
        parent.appendChild(tag);
      }
      // 時刻表示
      tag = document.createElement("text");
      tag.setAttribute("x", x);
      tag.setAttribute("y", top + timeLabelPosY);
      tag.setAttribute("text-anchor", "middle");
      tag.setAttribute("class", "kwg-graph-label");
      child = document.createTextNode(values[index].time);
      tag.appendChild(child);
      // 親要素に追加
      parent.appendChild(tag);
    }
  }
}

/********** **********/

/**
 * ツールチップタグ生成
 * @param {*} x 表示位置横座標
 * @param {*} y 表示位置縦座標
 * @param {*} w 表示幅
 * @param {*} h 表示高
 * @param {*} cls cssクラス名
 * @param {*} tip ツールチップ文字列
 * @return HTMLElement svgタグ
 */
function makeToolTipSvgTag(x, y, w, h, cls, tip) {
  let tag, title;

  tag = document.createElement("rect");
  tag.setAttribute("x", x);
  tag.setAttribute("y", y);
  tag.setAttribute("width", w);
  tag.setAttribute("height", h);
  tag.setAttribute("class", cls);
  title = document.createElement("title");
  title.appendChild(document.createTextNode(tip));
  tag.appendChild(title);

  return tag;
}

/**
 * 棒グラフタグ生成
 * @param {*} x 表示位置横座標
 * @param {*} y 表示位置縦座標
 * @param {*} w 表示幅
 * @param {*} h 表示高
 * @param {*} cls cssクラス名
 * @param {*} tip ツールチップ文字列
 * @return HTMLElement svgタグ
 */
function makeBarGraphSvgTag(x, y, w, h, cls, tip) {
  let tag, title;

  tag = document.createElement("rect");
  tag.setAttribute("x", x);
  tag.setAttribute("y", y);
  tag.setAttribute("width", w);
  tag.setAttribute("height", h);
  tag.setAttribute("class", cls);
  title = document.createElement("title");
  title.appendChild(document.createTextNode(tip));
  tag.appendChild(title);

  return tag;
}

/**
 * 線グラフタグ生成
 * @param {*} x1 表示開始位置横座標
 * @param {*} y1 表示開始位置縦座標
 * @param {*} x2 表示終端位置横座標
 * @param {*} y2 表示終端位置縦座標
 * @param {*} cls cssクラス名
 * @param {*} tip ツールチップ文字列
 * @return HTMLElement svgタグ
 */
function makeLineGraphSvgTag(x1, y1, x2, y2, cls, tip) {
  let tag, title;

  tag = document.createElement("line");
  tag.setAttribute("x1", x1);
  tag.setAttribute("y1", y1);
  tag.setAttribute("x2", x2);
  tag.setAttribute("y2", y2);
  tag.setAttribute("class", cls);
  title = document.createElement("title");
  title.appendChild(document.createTextNode(tip));
  tag.appendChild(title);

  return tag;
}

/**
 * グラフ観測点タグ生成
 * @param {*} cx 表示位置横座標
 * @param {*} cy 表示位置縦座標
 * @param {*} rd 半径サイズ
 * @param {*} cls cssクラス名
 * @param {*} tip ツールチップ文字列
 * @return HTMLElement svgタグ
 */
function makeCircleSvgTag(cx, cy, rd, cls, tip) {
  let tag, title;
  let r = circleSize;
  if (rd != null) {
    r = rd;
  }

  tag = document.createElement("circle");
  tag.setAttribute("cx", cx);
  tag.setAttribute("cy", cy);
  tag.setAttribute("r", r);
  tag.setAttribute("class", cls);
  title = document.createElement("title");
  title.appendChild(document.createTextNode(tip));
  tag.appendChild(title);

  return tag;
}

/**
 * グラフボーダータグ生成
 * @param {*} x1 表示開始位置横座標
 * @param {*} y1 表示開始位置縦座標
 * @param {*} x2 表示終端位置横座標
 * @param {*} y2 表示終端位置縦座標
 * @param {*} cls cssクラス名
 * @return HTMLElement svgタグ
 */
function makeBorderGraphSvgTag(x1, y1, x2, y2, cls) {
  let tag;

  tag = document.createElement("line");
  tag.setAttribute("x1", x1);
  tag.setAttribute("y1", y1);
  tag.setAttribute("x2", x2);
  tag.setAttribute("y2", y2);
  tag.setAttribute("class", cls);

  return tag;
}

/********** **********/

/**
 * ラベルSVGタグ生成
 * @param {*} width 親要素の幅
 * @param {*} height 親要素の高さ
 * @return HTMLElement svgタグ
 */
function makeLabelSvgTag(width, height) {
  // svgタグ要素生成
  let attrViewBox =
    "0 0 " + computeLabelWidth() + " " + computeHeight(width, height);
  let attrWidth = computeViewLabelWidth(width) + viewUnit;
  let attrHeight = height + viewUnit;
  // svgタグ生成
  let tag = document.createElement("svg");
  tag.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  tag.setAttribute("viewBox", attrViewBox);
  tag.setAttribute("width", attrWidth);
  tag.setAttribute("height", attrHeight);

  return tag;
}

/**
 * 左ラベルグループ生成
 * @param {*} parent 親要素
 * @return HTMLElement gタグ
 */
function addLeftLabelGroupTag(parent) {
  // gタグ生成
  let tag = document.createElement("g");
  tag.setAttribute("id", "svg-label");
  tag.setAttribute("class", "svg-left");
  // 親要素に追加
  parent.appendChild(tag);

  return tag;
}

/**
 * 左ラベル観測軸ラベル追加
 * @param {*} parent 親要素
 * @param {*} left グラフ描画領域左端
 * @param {*} top グラフ描画領域上端
 * @param {*} width グラフ領域の幅
 * @param {*} height グラフ領域の高さ
 * @param {*} max 観測軸最大値
 * @param {*} min 観測軸最小値
 * @param {*} disit 観測軸小数点以下桁
 * @param {*} div 分割数
 * @param {*} space 表示間隔
 * @param {*} cls cssクラス名
 */
function addLeftLabelYAxisLabelTag(
  parent,
  left,
  top,
  width,
  height,
  max,
  min,
  disit,
  div,
  space,
  cls
) {
  let tag, child;
  let label;
  let ls = ((max - min) / div) * space;
  let x = left + width - labelPosX;
  let y;
  let ys = (height / div) * space;
  let bottom = top + height;
  for (let i = 0; i <= div / space; i++) {
    label = Number(min + ls * i).toFixed(disit);
    y = bottom - ys * i;
    // 目盛表示
    tag = document.createElement("text");
    tag.setAttribute("x", x);
    tag.setAttribute("y", y + labelPosY);
    tag.setAttribute("text-anchor", "end");
    tag.setAttribute("class", cls);
    child = document.createTextNode(Number(label).toFixed(disit));
    tag.appendChild(child);
    // 親要素に追加
    parent.appendChild(tag);
  }
}

/**
 * 左ラベル観測軸タイトル追加
 * @param {*} parent 親要素
 * @param {*} left グラフ描画領域左端
 * @param {*} top グラフ描画領域上端
 * @param {*} width グラフ領域の幅
 * @param {*} height グラフ領域の高さ
 * @param {*} title 観測項目名
 * @param {*} unit 観測項目単位
 * @param {*} cls cssクラス名
 */
function addLeftLabelYAxisTitleTag(
  parent,
  left,
  top,
  width,
  height,
  title,
  unit,
  cls
) {
  let tag, child;
  let x = left + labelPosX / 2;
  let y = top + height / 2 + labelPosY + 40;
  // タイトル表示
  tag = document.createElement("text");
  tag.setAttribute("x", x);
  tag.setAttribute("y", y);
  tag.setAttribute("text-anchor", "middle");
  tag.setAttribute("writing-mode", "tb");
  tag.setAttribute("class", cls);
  if (unit != null && unit != "") {
    child = document.createTextNode(replaceString(title) + "[" + unit + "]");
  } else {
    child = document.createTextNode(replaceString(title));
  }
  tag.appendChild(child);
  // 親要素に追加
  parent.appendChild(tag);
}

/**
 * 左ラベル観測軸マーカー追加
 * @param {*} parent 親要素
 * @param {*} left グラフ描画領域左端
 * @param {*} top グラフ描画領域上端
 * @param {*} width グラフ領域の幅
 * @param {*} height グラフ領域の高さ
 * @param {*} max 観測軸最大値
 * @param {*} min 観測軸最小値
 * @param {*} value マーカー位置
 * @param {*} tip ツールチップ文字列
 * @param {*} cls cssクラス名
 */
function addLeftLabelYAxisMarkerTag(
  parent,
  left,
  top,
  width,
  height,
  max,
  min,
  value,
  tip,
  cls
) {
  let tag, title;
  let ys = height / (max - min);
  let bottom = top + height;
  let x1 = left + width - 1;
  let y1 = bottom - ys * (value - min);
  let x2 = x1 - markerSizeX;
  let y2 = y1 - markerSizeY;
  let x3 = x1 - markerSizeX;
  let y3 = y1 + markerSizeY;
  let points = x1 + " " + y1 + " " + x2 + " " + y2 + " " + x3 + " " + y3;

  // 目盛表示
  tag = document.createElement("polygon");
  tag.setAttribute("points", points);
  tag.setAttribute("class", cls);
  title = document.createElement("title");
  title.appendChild(document.createTextNode(tip));
  tag.appendChild(title);
  // 親要素に追加
  parent.appendChild(tag);
}

/**
 * 右ラベルグループ生成
 * @param {*} parent 親要素
 * @return HTMLElement gタグ
 */
function addRightLabelGroupTag(parent) {
  // gタグ生成
  let tag = document.createElement("g");
  tag.setAttribute("id", "svg-label");
  tag.setAttribute("class", "svg-right");
  // 親要素に追加
  parent.appendChild(tag);

  return tag;
}

/**
 * 右ラベル観測軸ラベル追加
 * @param {*} parent 親要素
 * @param {*} left グラフ描画領域左端
 * @param {*} top グラフ描画領域上端
 * @param {*} width グラフ領域の幅
 * @param {*} height グラフ領域の高さ
 * @param {*} max 観測軸最大値
 * @param {*} min 観測軸最小値
 * @param {*} disit 観測軸小数点以下桁
 * @param {*} div 分割数
 * @param {*} space 表示間隔
 * @param {*} cls cssクラス名
 */
function addRightLabelYAxisLabelTag(
  parent,
  left,
  top,
  width,
  height,
  max,
  min,
  disit,
  div,
  space,
  cls
) {
  let tag, child;
  let label;
  let ls = ((max - min) / div) * space;
  let x = left + labelPosX;
  let y;
  let ys = (height / div) * space;
  let bottom = top + height;
  for (let i = 0; i <= div / space; i++) {
    label = Number(min + ls * i).toFixed(disit);
    y = bottom - ys * i;
    // 目盛表示
    tag = document.createElement("text");
    tag.setAttribute("x", x);
    tag.setAttribute("y", y + labelPosY);
    tag.setAttribute("text-anchor", "start");
    tag.setAttribute("class", cls);
    child = document.createTextNode(label);
    tag.appendChild(child);
    // 親要素に追加
    parent.appendChild(tag);
  }
}

/**
 * 右ラベル観測軸タイトル追加
 * @param {*} parent 親要素
 * @param {*} left グラフ描画領域左端
 * @param {*} top グラフ描画領域上端
 * @param {*} width グラフ領域の幅
 * @param {*} height グラフ領域の高さ
 * @param {*} title 観測項目名
 * @param {*} unit 観測項目単位
 * @param {*} cls cssクラス名
 */
function addRightLabelYAxisTitleTag(
  parent,
  left,
  top,
  width,
  height,
  title,
  unit,
  cls
) {
  let tag, child;
  let x = left + width - labelPosX / 2;
  let y = top + height / 2 + labelPosY + 40;
  // タイトル表示
  tag = document.createElement("text");
  tag.setAttribute("x", x);
  tag.setAttribute("y", y);
  tag.setAttribute("text-anchor", "middle");
  tag.setAttribute("writing-mode", "tb");
  tag.setAttribute("class", cls);
  if (unit != null && unit != "") {
    child = document.createTextNode(replaceString(title) + "[" + unit + "]");
  } else {
    child = document.createTextNode(replaceString(title));
  }
  tag.appendChild(child);
  // 親要素に追加
  parent.appendChild(tag);
}

/**
 * 右ラベル観測軸タイトル追加(複数クラス指定)
 * @param {*} parent 親要素
 * @param {*} left グラフ描画領域左端
 * @param {*} top グラフ描画領域上端
 * @param {*} width グラフ領域の幅
 * @param {*} height グラフ領域の高さ
 * @param {*} titleList 観測項目名とクラス名の配列
 */
function addRightLabelYAxisTitleTagMulti(
  parent,
  left,
  top,
  width,
  height,
  titleList
) {
  let tag;
  let x = left + width - labelPosX / 2;
  let y = top + height / 2 + labelPosY + 40;
  // タイトル表示
  tag = document.createElement("text");
  tag.setAttribute("x", x);
  tag.setAttribute("y", y);
  tag.setAttribute("text-anchor", "middle");
  tag.setAttribute("writing-mode", "tb");

  // tspan毎にclassを指定
  titleList.forEach((title) => {
    let child = document.createElement("tspan");
    child.setAttribute("class", title.cls);
    child.appendChild(document.createTextNode(replaceString(title.nm)));
    tag.appendChild(child);
  });

  // 親要素に追加
  parent.appendChild(tag);
}

/**
 * 右ラベル観測軸マーカー追加
 * @param {*} parent 親要素
 * @param {*} left グラフ描画領域左端
 * @param {*} top グラフ描画領域上端
 * @param {*} width グラフ領域の幅
 * @param {*} height グラフ領域の高さ
 * @param {*} max 観測軸最大値
 * @param {*} min 観測軸最小値
 * @param {*} value マーカー位置
 * @param {*} tip ツールチップ文字列
 * @param {*} cls cssクラス名
 */
function addRightLabelYAxisMarkerTag(
  parent,
  left,
  top,
  width,
  height,
  max,
  min,
  value,
  tip,
  cls
) {
  let tag, title;
  let ys = height / (max - min);
  let bottom = top + height;
  let x1 = left + 1;
  let y1 = bottom - ys * (value - min);
  let x2 = x1 + markerSizeX;
  let y2 = y1 - markerSizeY;
  let x3 = x1 + markerSizeX;
  let y3 = y1 + markerSizeY;
  let points = x1 + " " + y1 + " " + x2 + " " + y2 + " " + x3 + " " + y3;

  // 目盛表示
  tag = document.createElement("polygon");
  tag.setAttribute("points", points);
  tag.setAttribute("class", cls);
  title = document.createElement("title");
  title.appendChild(document.createTextNode(tip));
  tag.appendChild(title);
  // 親要素に追加
  parent.appendChild(tag);
}

/********** **********/

/**
 * 凡例SVGタグ生成
 * @param {*} width 親要素の幅
 * @param {*} height 親要素の高さ
 * @param {*} w 凡例の幅
 * @param {*} h 凡例の高さ
 * @return HTMLElement svgタグ
 */
function makeLegendSvgTag(width, height, w, h) {
  let ratio = (viewWidth + viewLabelWidth * 2) / width;
  let ratioWH = w / h;
  let viewW = 0;
  let viewY = 0;
  if (w / ratio > 180) {
    viewW = 180;
    viewY = viewW / ratioWH;
  } else {
    viewW = w / ratio;
    viewY = h / ratio;
  }
  // svgタグ要素生成
  let attrViewBox = "0 0 " + w + " " + h;
  let attrWidth = viewW + viewUnit;
  let attrHeight = viewY + viewUnit;
  // svgタグ生成
  let tag = document.createElement("svg");
  tag.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  tag.setAttribute("viewBox", attrViewBox);
  tag.setAttribute("width", attrWidth);
  tag.setAttribute("height", attrHeight);

  return tag;
}

/**
 * 凡例グループ生成
 * @param {*} parent 親要素
 * @return HTMLElement gタグ
 */
function addLegendGroupTag(parent) {
  // gタグ生成
  let tag = document.createElement("g");
  tag.setAttribute("id", "svg-legend");
  tag.setAttribute("class", "svg-legend");
  // 親要素に追加
  parent.appendChild(tag);

  return tag;
}

/**
 * 凡例フレームタグ生成
 * @param {*} x 凡例描画開始横座標
 * @param {*} y 凡例描画開始縦座標
 * @param {*} w 凡例幅
 * @param {*} h 凡例高
 * @param {*} cls cssクラス名
 * @return HTMLElement svgタグ
 */
function makeLegendFrame(x, y, w, h, cls) {
  let tag;

  tag = document.createElement("rect");
  tag.setAttribute("x", x);
  tag.setAttribute("y", y);
  tag.setAttribute("width", w);
  tag.setAttribute("height", h);
  tag.setAttribute("rx", legendFrameRadius);
  tag.setAttribute("ry", legendFrameRadius);
  tag.setAttribute("class", cls);

  return tag;
}

/**
 * 凡例フレーム仕切線生成
 * @param {*} x1 凡例仕切線描画開始横座標
 * @param {*} y1 凡例仕切線描画開始縦座標
 * @param {*} x2 凡例仕切線描画終端横座標
 * @param {*} y2 凡例仕切線描画終端縦座標
 * @param {*} cls cssクラス名
 * @return HTMLElement svgタグ
 */
function makeLegendLine(x1, y1, x2, y2, cls) {
  let tag;

  tag = document.createElement("line");
  tag.setAttribute("x1", x1);
  tag.setAttribute("y1", y1);
  tag.setAttribute("x2", x2);
  tag.setAttribute("y2", y2);
  tag.setAttribute("class", cls);

  return tag;
}

/**
 * 凡例タイトル文字列追加
 * @param {*} parent 親要素
 * @param {*} x テキスト出力位置
 * @param {*} y テキスト出力位置
 * @param {*} title タイトル文字列
 * @param {*} tcls cssクラス名
 * @param {*} unit 単位文字列
 * @param {*} ucls cssクラス名
 * @return 次のテキスト出力位置(px)
 */
function addLegendTitle(parent, x, y, title, tcls, unit, ucls) {
  let tag, utag;
  // タイトルタグ追加
  tag = document.createElement("text");
  tag.setAttribute("x", x + legendTitlePosX);
  tag.setAttribute("y", y);
  tag.setAttribute("class", tcls);
  tag.appendChild(document.createTextNode(title));
  // 単位タグ追加
  if (unit != null && unit != "") {
    utag = document.createElement("tspan");
    utag.setAttribute("class", ucls);
    utag.appendChild(document.createTextNode(" [" + unit + "]"));
    tag.appendChild(utag);
  }
  parent.appendChild(tag);

  // 高さを返却
  return y + legendTextHeight;
}

/**
 * 凡例追加
 * @param {*} parent 親要素
 * @param {*} x テキスト出力位置
 * @param {*} y テキスト出力位置
 * @param {*} desc 説明文字列
 * @param {*} cls 説明文字列クラス名
 * @return 次のテキスト出力位置(px)
 */
function addLegendGraph(parent, x, y, desc, cls) {
  let tag;
  // タイトルタグ追加
  tag = document.createElement("text");
  tag.setAttribute("x", x + legendDescPosX);
  tag.setAttribute("y", y);
  tag.setAttribute("class", cls);
  tag.appendChild(document.createTextNode(desc));
  parent.appendChild(tag);

  // 高さを返却
  return y + legendTextHeight;
}

/**
 * 棒グラフ凡例追加
 * @param {*} parent 親要素
 * @param {*} x テキスト出力位置
 * @param {*} y テキスト出力位置
 * @param {*} bar 棒グラフクラス名
 * @param {*} desc 説明文字列
 * @param {*} cls 説明文字列クラス名
 * @return 次のテキスト出力位置(px)
 */
function addLegendBarGraph(parent, x, y, bar, desc, cls) {
  let tag;
  // タイトルタグ追加
  tag = document.createElement("rect");
  tag.setAttribute("x", x + legendGraphPosX);
  tag.setAttribute("y", y - 35);
  tag.setAttribute("width", 100);
  tag.setAttribute("height", 30);
  tag.setAttribute("class", bar);
  parent.appendChild(tag);
  tag = document.createElement("text");
  tag.setAttribute("x", x + legendDescPosX);
  tag.setAttribute("y", y);
  tag.setAttribute("class", cls);
  tag.appendChild(document.createTextNode(desc));
  parent.appendChild(tag);

  // 高さを返却
  return y + legendTextHeight;
}

/**
 * 棒グラフ単位付き凡例追加
 * @param {*} parent 親要素
 * @param {*} x テキスト出力位置
 * @param {*} y テキスト出力位置
 * @param {*} bar 棒グラフクラス名
 * @param {*} desc 説明文字列
 * @param {*} tcls 説明文字列クラス名
 * @param {*} unit 単位文字列
 * @param {*} ucls cssクラス名
 * @return 次のテキスト出力位置(px)
 */
function addLegendBarGraphUnit(parent, x, y, bar, desc, tcls, unit, ucls) {
  let tag, utag;
  // タイトルタグ追加
  tag = document.createElement("rect");
  tag.setAttribute("x", x + legendGraphPosX);
  tag.setAttribute("y", y - 35);
  tag.setAttribute("width", 100);
  tag.setAttribute("height", 30);
  tag.setAttribute("class", bar);
  parent.appendChild(tag);
  tag = document.createElement("text");
  tag.setAttribute("x", x + legendDescPosX);
  tag.setAttribute("y", y);
  tag.setAttribute("class", tcls);
  tag.appendChild(document.createTextNode(desc));
  // 単位タグ追加
  if (unit != null && unit != "") {
    utag = document.createElement("tspan");
    utag.setAttribute("class", ucls);
    utag.appendChild(document.createTextNode(" [" + unit + "]"));
    tag.appendChild(utag);
  }
  parent.appendChild(tag);

  // 高さを返却
  return y + legendTextHeight;
}

/**
 * 線グラフ凡例追加
 * @param {*} parent 親要素
 * @param {*} x テキスト出力位置
 * @param {*} y テキスト出力位置
 * @param {*} line 線グラフクラス名
 * @param {*} desc 説明文字列
 * @param {*} cls 説明文字列クラス名
 * @return 次のテキスト出力位置(px)
 */
function addLegendLineGraph(parent, x, y, line, desc, cls) {
  let tag;
  // タイトルタグ追加
  tag = document.createElement("line");
  tag.setAttribute("x1", x + legendGraphPosX);
  tag.setAttribute("y1", y - 20);
  tag.setAttribute("x2", x + legendGraphPosX + 100);
  tag.setAttribute("y2", y - 20);
  tag.setAttribute("class", line);
  parent.appendChild(tag);
  tag = document.createElement("text");
  tag.setAttribute("x", x + legendDescPosX);
  tag.setAttribute("y", y);
  tag.setAttribute("class", cls);
  tag.appendChild(document.createTextNode(desc));
  parent.appendChild(tag);

  // 高さを返却
  return y + legendTextHeight;
}

/**
 * 線グラフ単位付き凡例追加
 * @param {*} parent 親要素
 * @param {*} x テキスト出力位置
 * @param {*} y テキスト出力位置
 * @param {*} line 線グラフクラス名
 * @param {*} desc 説明文字列
 * @param {*} cls 説明文字列クラス名
 * @param {*} unit 単位文字列
 * @param {*} ucls cssクラス名
 * @return 次のテキスト出力位置(px)
 */
function addLegendLineGraphUnit(parent, x, y, line, desc, tcls, unit, ucls) {
  let tag, utag;
  // タイトルタグ追加
  tag = document.createElement("line");
  tag.setAttribute("x1", x + legendGraphPosX);
  tag.setAttribute("y1", y - 20);
  tag.setAttribute("x2", x + legendGraphPosX + 100);
  tag.setAttribute("y2", y - 20);
  tag.setAttribute("class", line);
  parent.appendChild(tag);
  tag = document.createElement("text");
  tag.setAttribute("x", x + legendDescPosX);
  tag.setAttribute("y", y);
  tag.setAttribute("class", tcls);
  tag.appendChild(document.createTextNode(desc));
  // 単位タグ追加
  if (unit != null && unit != "") {
    utag = document.createElement("tspan");
    utag.setAttribute("class", ucls);
    utag.appendChild(document.createTextNode(" [" + unit + "]"));
    tag.appendChild(utag);
  }
  parent.appendChild(tag);

  // 高さを返却
  return y + legendTextHeight;
}

/**
 * 観測所情報追加
 * @param {*} parent 親要素
 * @param {*} obsNm 観測所名
 * @param {*} rsysNm 水系名
 * @param {*} rvrNm 河川名
 * @param {*} obsDt 観測日時
 * @return 観測所情報の高さ(px)
 */
function addObsInfo(parent, obsNm, rsysNm, rvrNm, obsDt) {
  let g, tag;
  const cls = "kwg-obsinfo-label";
  const txt = obsNm + "  " + rsysNm + " " + rvrNm;

  // gタグ生成
  g = document.createElement("g");
  g.setAttribute("id", "svg-obsinfo");
  g.setAttribute("class", "svg-obsinfo");
  parent.appendChild(g);

  // 観測所情報タグ追加
  tag = document.createElement("text");
  tag.setAttribute("x", obsInfoPosX);
  tag.setAttribute("y", obsInfoHeight - 20);
  tag.setAttribute("class", cls);
  tag.appendChild(document.createTextNode(txt));
  g.appendChild(tag);
  tag = document.createElement("text");
  tag.setAttribute("x", viewWidth + viewLabelWidth * 2 - obsInfoPosX);
  tag.setAttribute("y", obsInfoHeight - 20);
  tag.setAttribute("text-anchor", "end");
  tag.setAttribute("class", cls);
  tag.appendChild(document.createTextNode(obsDt));
  g.appendChild(tag);

  // 高さを返却
  return obsInfoHeight;
}

/********** **********/

/**
 * スタイルタグ生成
 * @param {*} parent 親要素
 * @param {*} rule スタイル文字列
 * @return 親要素
 */
function addStyleTag(parent, rule) {
  // styleタグ要素生成
  let child = document.createTextNode(rule);
  // defs/styleタグ生成
  let defs = document.createElement("defs");
  let style = document.createElement("style");
  style.type = "text/css";
  //  style.setAttribute('scoped', 'scoped');
  style.appendChild(child);
  defs.appendChild(style);
  // 親要素に追加
  parent.appendChild(defs);

  return parent;
}

/********** **********/

/**
 * イメージグラフSVGタグ生成
 * @param {*} width 親要素の幅
 * @param {*} height 親要素の高さ
 * @param {*} legendX 凡例の幅
 * @return HTMLElement svgタグ
 */
function makeImageSvgTag(width, height, legendX) {
  // svgタグ要素生成
  let attrViewBox =
    "0 0 " +
    (computeGraphWidth(false) + computeLabelWidth() * 2 + legendX) +
    " " +
    computeHeight(width - computeImageLegendWidth(width, legendX), height);
  let attrWidth = width + viewUnit;
  let attrHeight = height + viewUnit;
  // svgタグ生成
  let tag = document.createElement("svg");
  tag.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  tag.setAttribute("viewBox", attrViewBox);
  tag.setAttribute("width", attrWidth);
  tag.setAttribute("height", attrHeight);
  // styleタグ追加
  addStyleTag(tag, styleCommon);

  return tag;
}

/********** **********/

// スクロール回数
const scrollStep = 50;
// スクロール間隔(ms)
const scrollTime = 50;
// スクロール情報リスト
// 複数グラフが並べられる事を考慮し配列で保持
let scrollDataList = [];

/**
 * グラフスクロール開始
 * @param {*} graph スクロール対象の要素
 */
function startScrollGraph(graph) {
  let existIdx = -1;
  // 同一idグラフのスクロール停止
  for (let sCnt = 0; sCnt < scrollDataList.length; sCnt++) {
    if (scrollDataList[sCnt].id == graph.id) {
      stopScrollGraph(scrollDataList[sCnt].id);
      existIdx = sCnt;
      break;
    }
  }

  if (graph != null) {
    let scrollData = null;
    if (existIdx >= 0) {
      // 既に同一IDが存在する場合は初期化
      // スクロール対象のid
      scrollDataList[existIdx].id = graph.id;
      // スクロール位置
      scrollDataList[existIdx].scrollPos = 0;
      // スクロール完了位置
      scrollDataList[existIdx].scrollEnd = graph.scrollWidth;
      // スクロール対象の要素
      scrollDataList[existIdx].scrollGraph = graph;

      scrollData = scrollDataList[existIdx];
    } else {
      // 存在しない場合は新たに追加
      scrollData = {
        id: graph.id,
        scrollPos: 0,
        scrollEnd: graph.scrollWidth,
        scrollGraph: graph,
      };
      scrollDataList.push(scrollData);
    }

    // スクロール
    const scrollTimerId = setInterval(
      (scrollObj) => {
        if (scrollObj.scrollPos > scrollStep || scrollObj.scrollGraph == null) {
          stopScrollGraph(scrollObj.id);
        } else {
          scrollObj.scrollGraph.scrollLeft =
            (scrollObj.scrollEnd * scrollObj.scrollPos) / scrollStep;
          scrollObj.scrollPos = scrollObj.scrollPos + 1;
        }
      },
      scrollTime,
      scrollData
    );
    // タイマーIDを設定
    scrollData.scrollTimerId = scrollTimerId;
  }
}

/**
 * グラフスクロール停止
 * @param {*} id 停止対象グラフのid
 */
function stopScrollGraph(id) {
  scrollDataList.forEach((obj) => {
    // id未指定の場合は全て停止、id指定の場合は対象のみ停止
    if (!id || obj.id == id) {
      if (obj.scrollTimerId != 0) {
        clearInterval(obj.scrollTimerId);
        obj.scrollTimerId = 0;
        obj.scrollPos = scrollStep;
        obj.scrollEnd = 0;
        obj.scrollGraph = null;
      }
    }
  });
}
